import React, { useEffect } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import PageShell from '../components/PageShell';
import PageSection from '../components/page/PageSection';
import InlineCta from '../components/page/InlineCta';
import { tie } from '../components/page/typography';
import ArticleMeta from '../components/blog/ArticleMeta';
import ArticleToc from '../components/blog/ArticleToc';
import RelatedService from '../components/blog/RelatedService';
import KeyPoint from '../components/blog/KeyPoint';
import RiskNote from '../components/blog/RiskNote';
import StepGroup from '../components/blog/StepGroup';
import SourceList from '../components/blog/SourceList';
import ArticleHelp from '../components/blog/ArticleHelp';
import { bodyText } from '../components/blog/prose';
import { articleSections, SECTION_IDS, SOURCES_TITLE, todoGroupId } from '../components/blog/sections';
import { findArticle } from '../content/blog';
import { findService } from '../content/services';
import { SITE, BRAND } from '../components/Seo';

const BANNER = '/images/service-documents-f1b5a713.jpg';

// Відступ між розділами статті менший, ніж між розділами сторінки послуги:
// тут один суцільний текст, і великі паузи рвали б його на окремі блоки.
const SECTION_SPACING = 'mt-14 lg:mt-16';

const BlogArticlePage = () => {
  const { slug } = useParams();
  const { hash } = useLocation();
  const article = findArticle(slug);

  // Картка в блозі веде просто в розділ (/blog/…#ryzyk). Під час переходу
  // всередині сайту сторінку малює React, а не браузер, і до якоря сам
  // браузер не прокручує — тож прокручуємо ми, уже після першого рендеру.
  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target) target.scrollIntoView();
  }, [hash, slug]);

  if (!article) return <Navigate to="/blog" replace />;

  const related = article.related ? findService(article.related) : null;
  const url = `${SITE}/blog/${article.slug}`;
  const sections = articleSections(article);
  const [opening, keyParagraph, ...restIntro] = article.intro;

  // Остання ланка — сама стаття. Раніше тут стояла рубрика «Ліцензування»,
  // і в розмітці крихт виходило, що рубрика і є ця сторінка.
  const crumbs = [{ name: 'Блог', to: '/blog' }, { name: article.crumb }];

  // image Google рекомендує для Article і саме з нього бере картинку до
  // статті у видачі. Два формати — банер 16:10 і картка 1200×630, — бо різні
  // місця видачі обрізають зображення під різні пропорції.
  const schema = {
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: [SITE + BANNER, `${SITE}/og-image.png`],
    datePublished: article.date,
    dateModified: article.updated || article.date,
    inLanguage: 'uk-UA',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    author: { '@type': 'Organization', name: BRAND, url: SITE },
    publisher: {
      '@type': 'Organization',
      name: BRAND,
      url: SITE,
      logo: { '@type': 'ImageObject', url: `${SITE}/icon-512.png` }
    }
  };

  return (
    <PageShell
      title={article.metaTitle}
      description={article.metaDescription}
      path={`/blog/${article.slug}`}
      banner={BANNER}
      schema={schema}
      crumbs={crumbs}
      heading={article.title}
      lead={article.excerpt}
      cta={false}
    >
      {/*
        Колонка статті — 43rem від xl, тобто близько сімдесяти знаків у
        рядку; раніше рядок у 18px розтягувався на 48rem і доходив до
        дев'яноста. Праворуч — зміст і пов'язана послуга; вони їдуть разом зі
        сторінкою (CSS sticky, без стану в React), і з будь-якого місця
        статті можна перейти до потрібного розділу.

        На телефоні порядок у DOM і є порядком показу: рубрика, дати й
        звертання — одразу під банером (раніше дати стояли під усім текстом),
        далі зміст, далі стаття. Від lg зміст стає в праву колонку на обидва
        рядки сітки.
      */}
      <div className="page-container pt-8 pb-14 lg:pt-12 lg:pb-20">
        <article className="grid gap-y-8 lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-x-12 lg:gap-y-4 xl:grid-cols-[minmax(0,43rem)_20rem] xl:justify-between">
          {/* Звертання — рядком під датами, без жовтого кола зі знаком
              оклику: тривогу тепер несе лише врізка про ризик. Воно тут, а
              не в тексті нижче, щоб на телефоні стояти над змістом. */}
          <div className="min-w-0 lg:col-start-1 lg:row-start-1">
            <ArticleMeta article={article} />
            <p className="mt-5 font-display font-semibold text-slate-900 text-lg leading-snug">{tie(article.lede)}</p>
          </div>

          <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2">
            <div className="lg:sticky lg:top-28 space-y-4">
              <ArticleToc sections={sections} />
              {/* Пов'язана послуга під змістом — лише від xl: на 1024 колонка
                  вужча, текст картки рветься на вісім рядків, і разом зі
                  змістом блок вищий за екран ноутбука — низ картки ховався б
                  під краєм вікна, поки сторінка не догорнеться до кінця. */}
              {related && <RelatedService service={related} className="hidden xl:block" />}
            </div>
          </div>

          <div className="min-w-0 lg:col-start-1 lg:row-start-2">
            <p className={`${bodyText} text-slate-700`}>{tie(opening)}</p>

            {keyParagraph && <KeyPoint className="mt-8" text={keyParagraph} dates={article.keyDates} />}
            {restIntro.map((paragraph) => (
              <p key={paragraph.slice(0, 40)} className={`${bodyText} mt-6 text-slate-700`}>
                {tie(paragraph)}
              </p>
            ))}

            {/* Зміни — рядками з підзаголовками, а не маркованим списком із
                жирним початком: шість пунктів по три-шість рядків злипалися в
                один абзац, і знайти потрібну зміну можна було, лише
                перечитавши всі. */}
            <PageSection id={SECTION_IDS.changes} caption="Зміни" title={article.changesTitle} spacing={SECTION_SPACING}>
              <ul className="list-none m-0 p-0 divide-y divide-slate-200 border-y border-slate-200">
                {article.changes.map((change) => (
                  <li key={change.label} className="py-5 lg:py-6">
                    <h3 className="font-display text-lg font-bold leading-snug text-slate-900 text-balance">{tie(change.label)}</h3>
                    <p className={`${bodyText} mt-2 text-slate-700`}>{tie(change.text)}</p>
                  </li>
                ))}
              </ul>
            </PageSection>

            <PageSection id={SECTION_IDS.risk} caption="Ризик" title={article.riskTitle} spacing={SECTION_SPACING}>
              <RiskNote paragraphs={article.risk} />
            </PageSection>

            {/*
              Заклик — тут, де людина щойно дізналася, чим ризикує, і ще до
              кроків: частина читачів саме в цьому місці вирішує, що робити
              самим не буде. Колонка статті вузька для розкладки «текст
              ліворуч, кнопки праворуч», тож кнопки стоять під текстом і на
              широкому екрані.
            */}
            <InlineCta
              source="blog-article-mid"
              spacing="mt-12 lg:mt-14"
              className="xl:flex-col xl:items-stretch xl:gap-y-5"
            />

            <PageSection id={SECTION_IDS.todo} caption="Покроково" title={article.todoTitle} spacing={SECTION_SPACING}>
              <div className="space-y-5">
                {article.todo.map((group, i) => (
                  <StepGroup
                    key={group.title}
                    id={todoGroupId(i)}
                    title={group.title}
                    steps={group.steps}
                    tone={i === 0 ? 'warn' : 'ok'}
                  />
                ))}
              </div>

              {related && article.relatedLead && (
                <p className={`${bodyText} mt-8 text-slate-700`}>
                  {`${tie(article.relatedLead)} «`}
                  <Link
                    to={`/poslugy/${related.meta.slug}`}
                    className="font-semibold text-teal-700 underline decoration-teal-300 underline-offset-4 hover:text-teal-800 hover:decoration-teal-500 transition-colors duration-200"
                  >
                    {related.meta.h1}
                  </Link>
                  ».
                </p>
              )}
            </PageSection>

            {/*
              Першоджерела — посиланнями на zakon.rada.gov.ua, а не переказом:
              читач, який приймає рішення про свою ліцензію, має звірити норму
              сам, а не вірити нам на слово.
            */}
            <PageSection id={SECTION_IDS.sources} caption="Перевірте самі" title={SOURCES_TITLE} spacing={SECTION_SPACING}>
              <SourceList sources={article.sources} />
            </PageSection>

            {/* Від xl ця картка весь час видна в правій колонці; вужче вона
                стоїть тут, після тексту. */}
            {related && <RelatedService service={related} className="mt-10 xl:hidden" />}
          </div>
        </article>
      </div>

      <ArticleHelp title={article.actionsTitle} actions={article.actions} />
    </PageShell>
  );
};

export default BlogArticlePage;
